import type { Offer, OfferShippingDetails } from 'schema-dts'
import type { GiftcardAddon } from '~/composables/types/cart'
import { cartGetters } from '~/utils/cartGetters'
import { organizationReference } from '~/composables/schemaOrg/useOrganizationSchema'
import { getSchemaReturnPolicy } from '~/utils/schemaOrgHelpers'

// Giftcards are redeemable in Germany and Austria.
const GIFTCARD_COUNTRIES = ['DE', 'AT']

// The PDF arrives by email right away; card and box ship in 2-5 working days
// (same wording as the delivery options in the cart).
const deliveryDaysByAddon: Partial<
  Record<GiftcardAddon, { min: number; max: number }>
> = {
  PDF: { min: 0, max: 0 },
  CARD: { min: 2, max: 5 },
  BOX: { min: 2, max: 5 },
}

const days = (min: number, max: number) => ({
  '@type': 'QuantitativeValue' as const,
  minValue: min,
  maxValue: max,
  unitCode: 'DAY',
})

/**
 * One entry per delivery option shown in the buy box and country, priced like
 * the cart: the add-on price plus, for a card or box, the country's delivery
 * cost (in cents, from `v1/store/shipping/costs`). Special giftcards don't pay
 * the country cost, so they leave it out.
 */
export const getGiftcardShippingDetails = (
  countryDeliveryCosts: Partial<Record<string, number>> = {}
): OfferShippingDetails[] =>
  cartGetters.getGiftcardAddonList
    .filter((addon) => addon.is_visible && deliveryDaysByAddon[addon.id])
    .flatMap((addon) => {
      const delivery = deliveryDaysByAddon[addon.id]!
      return GIFTCARD_COUNTRIES.map((addressCountry) => {
        const countryCost =
          addon.id === 'PDF' ? 0 : (countryDeliveryCosts[addressCountry] ?? 0)
        return {
          '@type': 'OfferShippingDetails' as const,
          shippingRate: {
            '@type': 'MonetaryAmount' as const,
            value: (addon.price + countryCost) / 100,
            currency: 'EUR',
          },
          shippingDestination: {
            '@type': 'DefinedRegion' as const,
            addressCountry,
          },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime' as const,
            handlingTime: days(0, 0),
            transitTime: days(delivery.min, delivery.max),
          },
        }
      })
    })

// Event bookings are excluded from the withdrawal, giftcards are not.
export const giftcardReturnPolicy = getSchemaReturnPolicy(GIFTCARD_COUNTRIES)

export const getGiftcardOffer = ({
  price,
  url,
  sku,
  countryDeliveryCosts,
}: {
  price: number
  url: string
  sku?: string
  countryDeliveryCosts?: Partial<Record<string, number>>
}): Offer => ({
  '@type': 'Offer',
  ...(sku && { sku }),
  price: price.toFixed(2),
  priceCurrency: 'EUR',
  availability: 'https://schema.org/InStock',
  url,
  seller: organizationReference,
  shippingDetails: getGiftcardShippingDetails(countryDeliveryCosts),
  hasMerchantReturnPolicy: giftcardReturnPolicy,
})
